(() => {
  'use strict';

  const DEFAULTS = {
    selector: '.blog-product',
    carouselSelector: '.blog-product-carousel',
    carouselSectionId: 'blog-product-carousel',
    carouselDefaultLimit: 8,
    carouselMaxLimit: 12,
    searchLimit: 10,
    currencyFallback: 'EUR',
    hideOnError: true,
    showVendor: true,
    showVariant: true,
    toastDuration: 3500,
    strings: {
      recommended: 'Empfohlen',
      available: 'Auf Lager',
      unavailable: 'Derzeit nicht verfügbar',
      addToCart: 'In den Warenkorb',
      adding: 'Wird hinzugefügt…',
      added: 'Hinzugefügt ✓',
      viewProduct: 'Produkt ansehen',
      cart: 'Warenkorb',
      addedToCart: 'Zum Warenkorb hinzugefügt',
      error: 'Produkt konnte nicht geladen werden',
      notFound: 'Kein Produkt für EAN gefunden:',
      carouselKicker: 'Für dich ausgewählt',
      carouselTitle: 'Weitere Empfehlungen',
      manualCarouselTitle: 'Unsere Empfehlungen',
      viewAll: 'Alle ansehen',
      previous: 'Zurück',
      next: 'Weiter',
      carouselError: 'Produkte konnten nicht geladen werden'
    }
  };

  const userConfig = window.BlogProductCardConfig || {};
  const CONFIG = {
    ...DEFAULTS,
    ...userConfig,
    strings: {
      ...DEFAULTS.strings,
      ...(userConfig.strings || {})
    }
  };

  const ROOT = window.Shopify?.routes?.root || '/';
  const productCache = new Map();
  const eanCache = new Map();
  const carouselCache = new Map();
  let currencyPromise = null;

  function normalizeEan(value) {
    return String(value ?? '').trim().replace(/\s+/g, '');
  }

  function normalizeCollectionHandle(value) {
    return String(value ?? '')
      .trim()
      .replace(/^\/+|\/+$/g, '')
      .replace(/^collections\//, '')
      .split(/[?#]/)[0];
  }


  function classValueByPrefix(element, prefix) {
    const token = Array.from(element?.classList || []).find(
      (className) => className.startsWith(prefix) && className.length > prefix.length
    );
    return token ? token.slice(prefix.length) : '';
  }

  function eansFromClasses(element) {
    return Array.from(element?.classList || [])
      .filter((className) => className.startsWith('ean-') && className.length > 4)
      .map((className) => normalizeEan(className.slice(4)))
      .filter(Boolean);
  }

  function getCardEan(container) {
    return normalizeEan(container?.dataset?.ean || classValueByPrefix(container, 'ean-'));
  }

  function getCarouselCollection(container) {
    return normalizeCollectionHandle(
      container?.dataset?.collection || classValueByPrefix(container, 'collection-')
    );
  }

  function getCarouselEans(container) {
    const fromClasses = eansFromClasses(container);
    if (fromClasses.length) return fromClasses;

    // Kompatybilność wsteczna, gdyby gdzieś pozostał stary format data-ean.
    const oldValue = normalizeEan(container?.dataset?.ean || '');
    return oldValue ? [oldValue] : [];
  }

  function clampNumber(value, min, max, fallback) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    if (typeof url === 'object') {
      url = url.src || url.url || '';
    }
    return String(url).startsWith('//') ? `https:${url}` : String(url);
  }

  function getVariantImage(product, variant) {
    return normalizeImageUrl(
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_image?.src ||
      variant?.featured_image ||
      product?.featured_image ||
      product?.images?.[0] ||
      ''
    );
  }

  function getLocale() {
    return document.documentElement.lang || navigator.language || 'de-DE';
  }

  function getCurrency() {
    if (currencyPromise) return currencyPromise;

    const shopifyCurrency =
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.shop;

    if (shopifyCurrency) {
      currencyPromise = Promise.resolve(shopifyCurrency);
      return currencyPromise;
    }

    currencyPromise = fetch(`${ROOT}cart.js`, {
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Currency request failed');
        return response.json();
      })
      .then((cart) => cart.currency || CONFIG.currencyFallback)
      .catch(() => CONFIG.currencyFallback);

    return currencyPromise;
  }

  async function formatMoney(cents) {
    const currency = await getCurrency();
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(getLocale(), {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `${amount.toFixed(2).replace('.', ',')} ${currency}`;
    }
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    const promise = fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Product request failed: ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        productCache.delete(handle);
        throw error;
      });

    productCache.set(handle, promise);
    return promise;
  }

  async function findProductByEan(ean) {
    const normalized = normalizeEan(ean);
    if (!normalized) throw new Error('Empty EAN');

    if (eanCache.has(normalized)) return eanCache.get(normalized);

    const promise = (async () => {
      const params = new URLSearchParams({
        q: normalized,
        'resources[type]': 'product',
        'resources[limit]': String(CONFIG.searchLimit),
        'resources[options][unavailable_products]': 'show',
        'resources[options][fields]': 'variants.barcode'
      });

      const response = await fetch(`${ROOT}search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Search request failed: ${response.status}`);
      }

      const data = await response.json();
      const results = data?.resources?.results?.products || [];

      for (const result of results) {
        if (!result?.handle) continue;

        const product = await fetchProduct(result.handle);
        const variant = product?.variants?.find(
          (item) => normalizeEan(item.barcode) === normalized
        );

        if (variant) return { product, variant };
      }

      return null;
    })().catch((error) => {
      eanCache.delete(normalized);
      throw error;
    });

    eanCache.set(normalized, promise);
    return promise;
  }

  function renderSkeleton(container) {
    container.classList.add('bp-card', 'bp-card--loading');
    container.setAttribute('aria-busy', 'true');
    container.innerHTML = `
      <div class="bp-skeleton__media"></div>
      <div class="bp-skeleton__content">
        <span class="bp-skeleton__line bp-skeleton__line--small"></span>
        <span class="bp-skeleton__line bp-skeleton__line--title"></span>
        <span class="bp-skeleton__line bp-skeleton__line--title bp-skeleton__line--short"></span>
        <span class="bp-skeleton__line bp-skeleton__line--price"></span>
        <span class="bp-skeleton__button"></span>
      </div>
    `;
  }

  function productUrl(product, variant) {
    return `${ROOT}products/${encodeURIComponent(product.handle)}?variant=${encodeURIComponent(variant.id)}`;
  }

  async function renderProduct(container, product, variant) {
    const currentPrice = Number(variant.price || 0);
    const comparePrice = Number(variant.compare_at_price || 0);
    const hasDiscount = comparePrice > currentPrice;
    const discount = hasDiscount
      ? Math.round(((comparePrice - currentPrice) / comparePrice) * 100)
      : 0;

    const [price, compareAtPrice] = await Promise.all([
      formatMoney(currentPrice),
      hasDiscount ? formatMoney(comparePrice) : Promise.resolve('')
    ]);

    const url = productUrl(product, variant);
    const image = getVariantImage(product, variant);
    const title = escapeHtml(product.title);
    const vendor = escapeHtml(product.vendor || '');
    const variantTitle = escapeHtml(variant.title || '');
    const showVariant =
      CONFIG.showVariant &&
      variantTitle &&
      variantTitle.toLowerCase() !== 'default title';

    container.classList.remove('bp-card--loading');
    container.classList.add('bp-card--ready');
    container.removeAttribute('aria-busy');

    container.innerHTML = `
      <div class="bp-card__media-wrap">
        <a class="bp-card__media" href="${escapeHtml(url)}" aria-label="${title}">
          ${image
            ? `<img class="bp-card__image" src="${escapeHtml(image)}" alt="${title}" loading="lazy" decoding="async">`
            : `<div class="bp-card__image-placeholder" aria-hidden="true">◇</div>`}
        </a>
        ${hasDiscount ? `<span class="bp-card__discount">-${discount}%</span>` : ''}
      </div>

      <div class="bp-card__content">
        <div class="bp-card__eyebrow">
          <span class="bp-card__recommendation">${escapeHtml(CONFIG.strings.recommended)}</span>
          <span class="bp-card__availability ${variant.available ? 'is-available' : 'is-unavailable'}">
            <span class="bp-card__availability-dot" aria-hidden="true"></span>
            ${escapeHtml(variant.available ? CONFIG.strings.available : CONFIG.strings.unavailable)}
          </span>
        </div>

        ${CONFIG.showVendor && vendor ? `<div class="bp-card__vendor">${vendor}</div>` : ''}

        <a class="bp-card__title" href="${escapeHtml(url)}">${title}</a>
        ${showVariant ? `<div class="bp-card__variant">${variantTitle}</div>` : ''}

        <div class="bp-card__price-row">
          <span class="bp-card__price">${escapeHtml(price)}</span>
          ${hasDiscount ? `<span class="bp-card__compare-price">${escapeHtml(compareAtPrice)}</span>` : ''}
        </div>

        <div class="bp-card__actions">
          <button
            class="bp-card__add"
            type="button"
            data-variant-id="${escapeHtml(variant.id)}"
            ${variant.available ? '' : 'disabled'}
          >
            <span class="bp-card__cart-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path d="M3 4h2l2.25 10.15a2 2 0 0 0 1.95 1.57h7.92a2 2 0 0 0 1.95-1.57L21 7H7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="10" cy="20" r="1.4" fill="currentColor"/>
                <circle cx="18" cy="20" r="1.4" fill="currentColor"/>
              </svg>
            </span>
            <span class="bp-card__add-label" data-bp-add-label>${escapeHtml(variant.available ? CONFIG.strings.addToCart : CONFIG.strings.unavailable)}</span>
          </button>

          <a class="bp-card__view" href="${escapeHtml(url)}">
            ${escapeHtml(CONFIG.strings.viewProduct)}
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    `;

    const addButton = container.querySelector('.bp-card__add');
    if (addButton && variant.available) {
      addButton.addEventListener('click', () => addToCart(addButton, product, variant));
    }
  }

  async function addToCart(button, product, variant) {
    if (button.disabled) return;

    const label = button.querySelector('[data-bp-add-label], .bp-card__add-label');
    const previousText = label?.textContent || CONFIG.strings.addToCart;

    button.disabled = true;
    button.classList.add('is-adding');
    if (label) label.textContent = CONFIG.strings.adding;

    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }]
        })
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.description || payload?.message || 'Cart request failed');
      }

      button.classList.remove('is-adding');
      button.classList.add('is-added');
      if (label) label.textContent = CONFIG.strings.added;

      showToast(product.title);
      refreshKnownCartCounters();

      document.dispatchEvent(
        new CustomEvent('blog-product:added', {
          detail: { product, variant, response: payload }
        })
      );

      window.setTimeout(() => {
        button.classList.remove('is-added');
        button.disabled = false;
        if (label) label.textContent = previousText;
      }, 2200);
    } catch (error) {
      console.error('[BlogProductCard] Add to cart error:', error);
      button.classList.remove('is-adding');
      button.disabled = false;
      if (label) label.textContent = previousText;
      showToast(CONFIG.strings.error, true);
    }
  }

  async function refreshKnownCartCounters() {
    try {
      const response = await fetch(`${ROOT}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();

      const selectors = [
        '[data-cart-count]',
        '.cart-count',
        '#CartCount',
        '.cart-count-bubble [aria-hidden="true"]'
      ];

      document.querySelectorAll(selectors.join(',')).forEach((node) => {
        node.textContent = cart.item_count;
        node.removeAttribute('hidden');
      });
    } catch (_) {
      // Optional enhancement only.
    }
  }

  function showToast(message, isError = false) {
    document.querySelector('.bp-toast')?.remove();

    const toast = document.createElement('div');
    toast.className = `bp-toast${isError ? ' bp-toast--error' : ''}`;
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <span class="bp-toast__icon" aria-hidden="true">${isError ? '!' : '✓'}</span>
      <span class="bp-toast__content">
        <strong>${escapeHtml(isError ? CONFIG.strings.error : CONFIG.strings.addedToCart)}</strong>
        <span>${escapeHtml(message)}</span>
      </span>
      ${!isError ? `<a class="bp-toast__link" href="${escapeHtml(`${ROOT}cart`)}">${escapeHtml(CONFIG.strings.cart)} →</a>` : ''}
    `;

    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));

    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 250);
    }, CONFIG.toastDuration);
  }

  function renderError(container, ean, error) {
    console.warn(`[BlogProductCard] ${CONFIG.strings.notFound} ${ean}`, error || '');

    const shouldShow =
      !CONFIG.hideOnError ||
      window.Shopify?.designMode ||
      container.dataset.debug === 'true';

    if (!shouldShow) {
      container.remove();
      return;
    }

    container.classList.remove('bp-card--loading');
    container.classList.add('bp-card--error');
    container.removeAttribute('aria-busy');
    container.innerHTML = `
      <div class="bp-card__error-content">
        <strong>${escapeHtml(CONFIG.strings.error)}</strong>
        <span>${escapeHtml(CONFIG.strings.notFound)} ${escapeHtml(ean)}</span>
      </div>
    `;
  }

  async function initCard(container) {
    if (container.dataset.bpInitialized === 'true') return;

    const ean = getCardEan(container);
    if (!ean) return;

    container.dataset.bpInitialized = 'true';
    renderSkeleton(container);

    try {
      const match = await findProductByEan(ean);
      if (!match) {
        renderError(container, ean);
        return;
      }

      await renderProduct(container, match.product, match.variant);
    } catch (error) {
      renderError(container, ean, error);
    }
  }

  /* =========================================================
     COLLECTION CAROUSEL
     ========================================================= */

  function renderCarouselSkeleton(container) {
    container.classList.add('bp-carousel', 'is-loading');
    container.setAttribute('aria-busy', 'true');
    container.innerHTML = `
      <div class="bp-carousel__header">
        <div class="bp-carousel__heading">
          <span class="bp-carousel__kicker">${escapeHtml(CONFIG.strings.carouselKicker)}</span>
          <div class="bp-carousel__skeleton-title"></div>
        </div>
      </div>
      <div class="bp-carousel__skeleton-track" aria-hidden="true">
        ${Array.from({ length: 4 }, () => `
          <div class="bp-carousel__skeleton-card">
            <span class="bp-carousel__skeleton-media"></span>
            <span class="bp-carousel__skeleton-line"></span>
            <span class="bp-carousel__skeleton-line is-short"></span>
            <span class="bp-carousel__skeleton-price"></span>
          </div>
        `).join('')}
      </div>
    `;
  }

  function collectionUrl(handle) {
    return `${ROOT}collections/${encodeURIComponent(handle)}`;
  }

  async function fetchCarouselSection(handle) {
    if (carouselCache.has(handle)) return carouselCache.get(handle);

    const separator = collectionUrl(handle).includes('?') ? '&' : '?';
    const url = `${collectionUrl(handle)}${separator}section_id=${encodeURIComponent(CONFIG.carouselSectionId)}`;

    const promise = fetch(url, {
      headers: { Accept: 'text/html' }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Carousel section request failed: ${response.status}`);
        }
        return response.text();
      })
      .catch((error) => {
        carouselCache.delete(handle);
        throw error;
      });

    carouselCache.set(handle, promise);
    return promise;
  }

  function parseCarouselSource(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-bp-carousel-source]');
  }

  function updateCarouselControls(container) {
    const track = container.querySelector('[data-bp-carousel-track]');
    const prev = container.querySelector('[data-bp-carousel-prev]');
    const next = container.querySelector('[data-bp-carousel-next]');
    const progress = container.querySelector('[data-bp-carousel-progress]');

    if (!track) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const overflow = maxScroll > 3;
    const current = Math.max(0, Math.min(maxScroll, track.scrollLeft));

    if (prev) prev.disabled = !overflow || current <= 3;
    if (next) next.disabled = !overflow || current >= maxScroll - 3;

    if (progress) {
      const visibleRatio = track.scrollWidth > 0
        ? Math.min(1, track.clientWidth / track.scrollWidth)
        : 1;
      const thumbWidth = Math.max(18, visibleRatio * 100);
      const travel = 100 - thumbWidth;
      const position = maxScroll > 0 ? (current / maxScroll) * travel : 0;

      progress.style.setProperty('--bp-progress-width', `${thumbWidth}%`);
      progress.style.setProperty('--bp-progress-x', `${position}%`);
      progress.hidden = !overflow;
    }
  }

  function scrollCarousel(container, direction) {
    const track = container.querySelector('[data-bp-carousel-track]');
    if (!track) return;

    const firstCard = track.querySelector('[data-bp-carousel-card]');
    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '16') || 16;
    const cardWidth = firstCard?.getBoundingClientRect().width || track.clientWidth * 0.75;
    const cardsPerStep = Math.max(1, Math.floor(track.clientWidth / (cardWidth + gap)));
    const distance = (cardWidth + gap) * cardsPerStep * direction;

    track.scrollBy({
      left: distance,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  function bindCarousel(container) {
    const track = container.querySelector('[data-bp-carousel-track]');
    const prev = container.querySelector('[data-bp-carousel-prev]');
    const next = container.querySelector('[data-bp-carousel-next]');

    prev?.addEventListener('click', () => scrollCarousel(container, -1));
    next?.addEventListener('click', () => scrollCarousel(container, 1));

    track?.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => updateCarouselControls(container));
    }, { passive: true });

    container.querySelectorAll('[data-bp-carousel-add]').forEach((button) => {
      if (button.disabled) return;

      button.addEventListener('click', () => {
        const variantId = button.dataset.variantId;
        const productTitle = button.dataset.productTitle || '';
        if (!variantId) return;

        addToCart(
          button,
          { title: productTitle },
          { id: variantId }
        );
      });
    });

    updateCarouselControls(container);
  }

  async function buildManualCarouselCard(product, variant) {
    const currentPrice = Number(variant.price || 0);
    const comparePrice = Number(variant.compare_at_price || 0);
    const hasDiscount = comparePrice > currentPrice;
    const discount = hasDiscount
      ? Math.round(((comparePrice - currentPrice) / comparePrice) * 100)
      : 0;

    const [price, compareAtPrice] = await Promise.all([
      formatMoney(currentPrice),
      hasDiscount ? formatMoney(comparePrice) : Promise.resolve('')
    ]);

    const url = productUrl(product, variant);
    const image = getVariantImage(product, variant);
    const title = escapeHtml(product.title || '');
    const vendor = escapeHtml(product.vendor || '');
    const available = Boolean(variant.available);

    return `
      <article
        class="bp-carousel-card"
        data-bp-carousel-card
        data-product-id="${escapeHtml(product.id || '')}"
        data-variant-id="${escapeHtml(variant.id)}"
      >
        <div class="bp-carousel-card__media-wrap">
          <a class="bp-carousel-card__media" href="${escapeHtml(url)}" aria-label="${title}">
            ${image
              ? `<img class="bp-carousel-card__image" src="${escapeHtml(image)}" alt="${title}" loading="lazy" decoding="async">`
              : `<span class="bp-carousel-card__placeholder" aria-hidden="true">◇</span>`}
          </a>

          ${hasDiscount ? `<span class="bp-carousel-card__discount">-${discount}%</span>` : ''}

          <span
            class="bp-carousel-card__stock ${available ? 'is-available' : 'is-unavailable'}"
            title="${escapeHtml(available ? CONFIG.strings.available : CONFIG.strings.unavailable)}"
            aria-label="${escapeHtml(available ? CONFIG.strings.available : CONFIG.strings.unavailable)}"
          >
            <span class="bp-carousel-card__stock-dot" aria-hidden="true"></span>
          </span>
        </div>

        <div class="bp-carousel-card__body">
          ${vendor ? `<div class="bp-carousel-card__vendor">${vendor}</div>` : ''}

          <a class="bp-carousel-card__title" href="${escapeHtml(url)}">${title}</a>

          <div class="bp-carousel-card__footer">
            <div class="bp-carousel-card__price-wrap">
              <span class="bp-carousel-card__price">${escapeHtml(price)}</span>
              ${hasDiscount ? `<span class="bp-carousel-card__compare">${escapeHtml(compareAtPrice)}</span>` : ''}
            </div>

            <button
              class="bp-carousel-card__add"
              type="button"
              data-bp-carousel-add
              data-variant-id="${escapeHtml(variant.id)}"
              data-product-title="${title}"
              ${available ? '' : 'disabled'}
              aria-label="${title} ${escapeHtml(available ? CONFIG.strings.addToCart : CONFIG.strings.unavailable)}"
            >
              <span class="bp-carousel-card__add-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <path d="M3 4h2l2.25 10.15a2 2 0 0 0 1.95 1.57h7.92a2 2 0 0 0 1.95-1.57L21 7H7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="10" cy="20" r="1.35" fill="currentColor"/>
                  <circle cx="18" cy="20" r="1.35" fill="currentColor"/>
                </svg>
              </span>
              <span data-bp-add-label>${escapeHtml(available ? CONFIG.strings.addToCart : CONFIG.strings.unavailable)}</span>
            </button>
          </div>
        </div>
      </article>
    `;
  }

  function renderManualCarousel(container, cards) {
    if (!cards.length) throw new Error('No products found for supplied EANs');

    const title = CONFIG.strings.manualCarouselTitle;
    const kicker = CONFIG.strings.carouselKicker;

    container.classList.remove('is-loading');
    container.classList.add('is-ready');
    container.removeAttribute('aria-busy');

    container.innerHTML = `
      <div class="bp-carousel__header">
        <div class="bp-carousel__heading">
          <span class="bp-carousel__kicker">${escapeHtml(kicker)}</span>
          <h3 class="bp-carousel__title">${escapeHtml(title)}</h3>
        </div>

        <div class="bp-carousel__header-actions">
          <div class="bp-carousel__arrows" aria-label="Karussell-Navigation">
            <button class="bp-carousel__arrow" type="button" data-bp-carousel-prev aria-label="${escapeHtml(CONFIG.strings.previous)}">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M14.5 5.5 8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button class="bp-carousel__arrow" type="button" data-bp-carousel-next aria-label="${escapeHtml(CONFIG.strings.next)}">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9.5 5.5 6.5 6.5-6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div class="bp-carousel__viewport">
        <div class="bp-carousel__track" data-bp-carousel-track tabindex="0" aria-label="${escapeHtml(title)}">
          ${cards.join('')}
        </div>
      </div>

      <div class="bp-carousel__progress" data-bp-carousel-progress aria-hidden="true">
        <span class="bp-carousel__progress-thumb"></span>
      </div>
    `;

    bindCarousel(container);
  }

  async function initManualCarousel(container, eans) {
    const selectedEans = eans.slice(0, CONFIG.carouselMaxLimit);
    const matches = await Promise.all(
      selectedEans.map(async (ean) => {
        try {
          return await findProductByEan(ean);
        } catch (error) {
          console.warn(`[BlogProductCarousel] EAN ${ean} konnte nicht geladen werden`, error);
          return null;
        }
      })
    );

    const validMatches = matches.filter(Boolean);
    if (!validMatches.length) throw new Error('No matching EAN products');

    const cards = await Promise.all(
      validMatches.map(({ product, variant }) => buildManualCarouselCard(product, variant))
    );

    renderManualCarousel(container, cards);
  }

  function renderCarousel(container, source, handle) {
    const limit = clampNumber(
      container.dataset.limit,
      1,
      CONFIG.carouselMaxLimit,
      CONFIG.carouselDefaultLimit
    );

    const sourceCards = Array.from(source.querySelectorAll('[data-bp-carousel-card]')).slice(0, limit);
    if (!sourceCards.length) throw new Error('Collection has no products');

    const collectionTitle = source.dataset.collectionTitle || '';
    const collectionHref = source.dataset.collectionUrl || collectionUrl(handle);
    const customTitle = String(container.dataset.title || '').trim();
    const customKicker = String(container.dataset.kicker || '').trim();
    const title = customTitle || CONFIG.strings.carouselTitle;
    const kicker = customKicker || CONFIG.strings.carouselKicker;

    container.classList.remove('is-loading');
    container.classList.add('is-ready');
    container.removeAttribute('aria-busy');

    container.innerHTML = `
      <div class="bp-carousel__header">
        <div class="bp-carousel__heading">
          <span class="bp-carousel__kicker">${escapeHtml(kicker)}</span>
          <h3 class="bp-carousel__title">${escapeHtml(title)}</h3>
          ${collectionTitle ? `<span class="bp-carousel__collection-name">${escapeHtml(collectionTitle)}</span>` : ''}
        </div>

        <div class="bp-carousel__header-actions">
          <a class="bp-carousel__view-all" href="${escapeHtml(collectionHref)}">
            ${escapeHtml(CONFIG.strings.viewAll)}
            <span aria-hidden="true">↗</span>
          </a>

          <div class="bp-carousel__arrows" aria-label="Karussell-Navigation">
            <button class="bp-carousel__arrow" type="button" data-bp-carousel-prev aria-label="${escapeHtml(CONFIG.strings.previous)}">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M14.5 5.5 8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button class="bp-carousel__arrow" type="button" data-bp-carousel-next aria-label="${escapeHtml(CONFIG.strings.next)}">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9.5 5.5 6.5 6.5-6.5 6.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div class="bp-carousel__viewport">
        <div class="bp-carousel__track" data-bp-carousel-track tabindex="0" aria-label="${escapeHtml(title)}">
          ${sourceCards.map((card) => card.outerHTML).join('')}
        </div>
      </div>

      <div class="bp-carousel__progress" data-bp-carousel-progress aria-hidden="true">
        <span class="bp-carousel__progress-thumb"></span>
      </div>
    `;

    bindCarousel(container);
  }

  function renderCarouselError(container, handle, error) {
    console.warn(`[BlogProductCarousel] ${CONFIG.strings.carouselError}: ${handle}`, error || '');

    const shouldShow =
      !CONFIG.hideOnError ||
      window.Shopify?.designMode ||
      container.dataset.debug === 'true';

    if (!shouldShow) {
      container.remove();
      return;
    }

    container.classList.remove('is-loading');
    container.classList.add('is-error');
    container.removeAttribute('aria-busy');
    container.innerHTML = `
      <div class="bp-carousel__error">
        <strong>${escapeHtml(CONFIG.strings.carouselError)}</strong>
        <span>${escapeHtml(handle)}</span>
      </div>
    `;
  }

  async function initCarousel(container) {
    if (container.dataset.bpCarouselInitialized === 'true') return;

    const eans = getCarouselEans(container);
    const handle = getCarouselCollection(container);

    // Brak konfiguracji = zwykły pusty div. Nie dotykamy go.
    if (!eans.length && !handle) return;

    container.dataset.bpCarouselInitialized = 'true';
    renderCarouselSkeleton(container);

    try {
      // Jeśli podano EAN-y, to one są źródłem karuzeli.
      // Kolejność klas = kolejność produktów.
      if (eans.length) {
        await initManualCarousel(container, eans);
        return;
      }

      const html = await fetchCarouselSection(handle);
      const source = parseCarouselSource(html);
      if (!source) throw new Error('Carousel source not found in rendered section');
      renderCarousel(container, source, handle);
    } catch (error) {
      renderCarouselError(container, handle || eans.join(', '), error);
    }
  }

  function initAll(root = document) {
    root.querySelectorAll?.(CONFIG.selector).forEach(initCard);
    root.querySelectorAll?.(CONFIG.carouselSelector).forEach(initCarousel);

    if (root.matches?.(CONFIG.selector)) initCard(root);
    if (root.matches?.(CONFIG.carouselSelector)) initCarousel(root);
  }

  function start() {
    initAll(document);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) initAll(node);
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    let resizeFrame = null;
    window.addEventListener('resize', () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        document.querySelectorAll(CONFIG.carouselSelector).forEach(updateCarouselControls);
      });
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
